/**
 * Blog Post — /blog/:slug
 *
 * Single-post reading view. Long-form-friendly typography (max-width
 * 720px, line-height 1.7, body 18px). Renders post.content() from
 * src/data/blog-posts.js.
 *
 * 404 fallback if slug doesn't match a real post — keeps the user
 * inside the marketing chrome instead of dropping them into the
 * generic <NotFound /> page (better continuity).
 */

import React from 'react';
import { Link, useParams } from 'react-router-dom';
import TrialButton from '../../components/TrialSignup/TrialButton';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/ThemeContext';
import SEO, { generateArticleSchema } from '../../components/SEO';
import { PremiumFooter } from '../../components/premium';
import { getPostBySlug, getRecentPosts } from '../../data/blog-posts';
// HomePremium.css owns the .home-nav-premium / .home-nav-logo-img
// styles used by the nav below — explicit import for cold lazy-load.
import '../HomePremium.css';
import './BlogPagePremium.css';
import { PLAN_PRICES, SERVE_NEXT_PRICE, SERVE_LAUNCH_PRICE, TRIAL_DAYS } from '../../utils/offerTerms';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const PremiumNav = ({ currentUser, isDark, toggleTheme }) => (
  <nav className="home-nav-premium">
    <Link to="/" className="home-nav-logo-premium">
      <img
        src={isDark ? '/Canira White Logo updated.png' : '/Canira Black Logo updated .png'}
        alt="Canira"
        className="home-nav-logo-img"
      />
    </Link>
    <div className="home-nav-links-premium">
      <Link to="/features">Platform</Link>
      <Link to="/pricing">Pricing</Link>
      <Link to="/demo">Book Demo</Link>
    </div>
    <div className="home-nav-cta-premium">
      {currentUser ? (
        <Link to="/dashboard" className="home-nav-login-premium">Dashboard</Link>
      ) : (
        <>
          <Link to="/login" className="home-nav-login-premium">Login</Link>
          <TrialButton className="home-nav-trial-premium"><span className="nav-trial-long">Start 14-day trial</span><span className="nav-trial-short">Free trial</span></TrialButton>
        </>
      )}
      <button
        className="home-nav-theme-toggle"
        onClick={toggleTheme}
        aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      >
        {isDark ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" />
            <line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" />
            <line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        )}
      </button>
    </div>
  </nav>
);

const BlogPost = () => {
  const { slug } = useParams();
  const { currentUser } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const post = getPostBySlug(slug);

  // 404 inside the marketing chrome — keeps the reader on the site
  // instead of bouncing them to a generic NotFound page. Especially
  // useful for shared/old links that may have been renamed.
  if (!post) {
    return (
      <div className="blog-page-premium">
        <SEO
          title="Post not found — Canira"
          description="The post you're looking for doesn't exist or has been moved."
          canonicalUrl="/blog"
          noIndex
        />
        <PremiumNav currentUser={currentUser} isDark={isDark} toggleTheme={toggleTheme} />
        <main className="blog-post-container">
          <div className="blog-post-404">
            <h1>Post not found</h1>
            <p>
              The post you're looking for doesn't exist or has been moved.
              Browse the rest of the founder log:
            </p>
            <Link to="/blog" className="blog-cta-primary">
              ← Back to all posts
            </Link>
          </div>
        </main>
        <PremiumFooter />
      </div>
    );
  }

  // Pull a couple of "more posts" for the bottom — exclude the current.
  const morePosts = getRecentPosts(50)
    .filter((p) => p.slug !== post.slug)
    .slice(0, 3);

  return (
    <div className="blog-page-premium">
      {/* SEO appends "| Canira" itself — adding "— Canira" here too
          produced "… — Canira | Canira" titles in the prerendered HTML.
          seoTitle (optional, per post) keeps the <title> tag within
          search engines' ~60-char display limit while the on-page H1
          keeps the longer expressive title — Bing flagged "Title too
          long" on posts whose full title passed 60 chars with the
          suffix. */}
      <SEO
        title={post.seoTitle || post.title}
        description={post.excerpt}
        keywords={post.tags ? post.tags.join(', ') : undefined}
        canonicalUrl={`/blog/${post.slug}`}
        ogType="article"
        structuredData={generateArticleSchema(post)}
      />

      <PremiumNav currentUser={currentUser} isDark={isDark} toggleTheme={toggleTheme} />

      <main className="blog-post-container">
        <header className="blog-post-hero">
          {post.tags && post.tags[0] && (
            <div className="blog-post-eyebrow">{post.tags[0]}</div>
          )}
          <h1 className="blog-post-title">{post.title}</h1>
          <div className="blog-post-meta">
            <span className="blog-post-meta-author">
              <strong>{post.author}</strong>
              {post.authorRole && (
                <>
                  {' '}
                  <span className="blog-post-meta-role">· {post.authorRole}</span>
                </>
              )}
            </span>
            <span className="blog-post-meta-dot" aria-hidden="true">·</span>
            <span>{formatDate(post.publishedAt)}</span>
            <span className="blog-post-meta-dot" aria-hidden="true">·</span>
            <span>{post.readingTime}</span>
          </div>
        </header>

        <article className="blog-post-body">
          {post.content()}
        </article>

        {/* Author bio + CTA in a single footer block */}
        <footer className="blog-post-footer">
          <div className="blog-post-author-card">
            <div className="blog-post-author-avatar" aria-hidden="true">CE</div>
            <div className="blog-post-author-text">
              <h3>{post.author}</h3>
              {post.authorRole && <p className="blog-post-author-role">{post.authorRole}</p>}
              <p className="blog-post-author-bio">
                Building Canira so creators don't have to stitch seven
                tools together to run one webinar. Replies to{' '}
                <a href="mailto:chante@canira.io">chante@canira.io</a>{' '}
                personally while we're in beta.
              </p>
            </div>
          </div>

          <div className="blog-post-cta-card">
            <h3>Founding Member seats</h3>
            <p>
              The first 25 pay ${PLAN_PRICES.professional}/mo, locked for life (${SERVE_NEXT_PRICE} after them,
              ${SERVE_LAUNCH_PRICE} at launch). Direct line to the founder. Starts with a {TRIAL_DAYS}-day free trial.
            </p>
            <Link to="/pricing" className="blog-cta-primary">
              Claim a founding seat
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </footer>

        {morePosts.length > 0 && (
          <section className="blog-post-more" aria-label="More posts">
            <h2>More from the founder log</h2>
            <div className="blog-post-grid">
              {morePosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="blog-card"
                >
                  <div className="blog-card-meta">
                    <span className="blog-card-date">{formatDate(p.publishedAt)}</span>
                    <span className="blog-card-dot" aria-hidden="true">·</span>
                    <span className="blog-card-reading">{p.readingTime}</span>
                  </div>
                  <h3 className="blog-card-title">{p.title}</h3>
                  <p className="blog-card-excerpt">{p.excerpt}</p>
                  <span className="blog-card-read">
                    Read
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <PremiumFooter />
    </div>
  );
};

export default BlogPost;
